<template>
  <loader :is-loading="isLoading" color="primary" :full-page="true">
    <v-flex>
      <v-card flat id="employee-card" class="pa-5">
        <v-card-title id="employee-title" class="form-title">
          <span>{{ $t(`components.employeeForm.guest.${mode}.title`) }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="forceClear()">mdi-close</v-icon>
        </v-card-title>
        <v-card-text id="employee-form" class="form-content">
          <v-row>
            <v-col cols="6">
              <span class="text-uppercase input-label mb-2">{{ $t(`components.employeeForm.default.firstName`) }}</span>
              <v-text-field
                :placeholder="$t(`components.employeeForm.default.givenName`)"
                :class="{ 'employee-field-error': $v.guest.givenName.$error }"
                v-model="guest.givenName"
                class="employee-field mt-5"
                height="44"
                outlined
                required
                clearable
                hide-details
                @input="$v.guest.givenName.$touch()"
                @blur="$v.guest.givenName.$touch()"
              >
                <template v-if="$v.guest.givenName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.guest.givenName"/>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="6">
              <span class="text-uppercase input-label mb-2">{{ $t(`components.employeeForm.default.lastName`) }}</span>
              <v-text-field
                v-model="guest.familyName"
                :class="{ 'employee-field-error': $v.guest.familyName.$error }"
                :placeholder="$t(`components.employeeForm.default.familyName`)"
                class="employee-field mt-5"
                outlined
                clearable
                required
                hide-details
                no-filter
                @input="$v.guest.familyName.$touch()"
                @blur="$v.guest.familyName.$touch()"
              >
                <template v-if="$v.guest.familyName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.guest.familyName"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.email`) }}</span>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="guest.email"
                :placeholder="$t(`components.employeeForm.default.emailPlaceholder`)"
                :class="{ 'employee-field-error': $v.guest.email.$error }"
                class="employee-field email-input"
                outlined
                required
                clearable
                hide-details
                no-filter
                @input="$v.guest.email.$touch()"
                @blur="$v.guest.email.$touch()"
              >
                <template v-if="$v.guest.email.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.guest.email"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="input-label">{{ $t(`components.employeeForm.default.phone`) }}</span>
            </v-col>

            <v-col cols="12">
              <vue-tel-input
                :disabled="mode === 'edit'"
                ref="employee-phone-input"
                filled
                mode="international"
                :class="{ 'employee-field-error': $v.guest.phone.$error }"
                :autoDefaultCountry="false"
                :defaultCountry="countryCode"
                :dropdownOptions="{
                  showDialCodeInList: true,
                  showDialCodeInSelection: true,
                  showFlags: true,
                  width: '240px',
                }"
                :inputOptions="{ showDialCode: false }"
                v-model="guest.phone"
                @input="$v.guest.phone.$touch()"
                @blur="$v.guest.phone.$touch()"
              >
                <template v-if="$v.guest.phone.$error && !$v.guest.phone.required" v-slot:icon-right>
                  <div class="mr-2 mt-2">
                    <error-tooltip :validator="$v.guest.phone"/>
                  </div>
                </template>
              </vue-tel-input>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="mt-4">
          <v-btn
            class="text-capitalize"
            color="grey darken-1"
            text
            @click="forceClear()"
          >
            {{ $t(`components.employeeForm.guest.${mode}.cancel`) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="text-capitalize white--text"
            color="#333333"
            depressed
            :disabled="$v.guest.$invalid || isLoading || formChanged"
            @click="submitDialog"
          >
            {{ $t(`components.employeeForm.guest.${mode}.agree`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </loader>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEqual } from 'lodash';

import employeeValidation from '@/helpers/rules/employeeValidation';

import ErrorTooltip from '@/components/elements/ErrorTooltip.vue';

const blankState = {
  businessAccountId: null,
  givenName: null,
  familyName: null,
  phone: '',
  email: null,
};

export default {
  name: 'CreateGuestForm',
  props: {
    mode: {
      type: String,
      default: 'add',
    },
    guestData: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    ErrorTooltip,
  },
  validations: {
    ...employeeValidation,
  },
  data() {
    return {
      guest: {},
      isLoading: false,
      formChanged: false,
    };
  },
  watch: {
    guestData: {
      handler() {
        if (this.mode === 'edit') {
          this.guest = { ...this.guestData };
        } else {
          this.guest = { ...blankState, ...this.guestData };
        }
      },
      immediate: true,
    },
    guest: {
      handler(val) {
        if (this.mode === 'edit') {
          this.formChanged = isEqual(this.getMutableFields(val), this.getMutableFields(this.guestData));
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters({
      countryCode: 'bookingChannel/defaultCountry',
    }),
    ...mapGetters('corporations', ['getSelectedCorporationId']),
    getActionURL() {
      return this.mode === 'edit' ? 'businessGuest/updateGuestPassenger' : 'businessGuest/createGuestPassenger';
    },
    getActionPayload() {
      if (this.mode === 'edit') {
        return {
          id: this.guestData.id,
          payload: {
            email: this.guest.email,
            familyName: this.guest.familyName,
            givenName: this.guest.givenName,
          },
        };
      }

      return {
        email: this.guest.email,
        familyName: this.guest.familyName,
        givenName: this.guest.givenName,
        phoneNumber: this.guest.phone.replaceAll(' ', ''),
        businessAccountId: this.getSelectedCorporationId,
      };
    },
  },
  methods: {
    forceClear(submitted, res) {
      this.guest = { ...blankState };

      this.isLoading = false;

      this.$v.$reset();
      this.$emit('closeDialog', submitted, res);
    },
    getMutableFields(val) {
      return {
        givenName: val.givenName,
        familyName: val.familyName,
        email: val.email,
      };
    },
    submitDialog() {
      this.$v.guest.$touch();

      if (this.$v.guest.$invalid) {
        return;
      }

      this.isLoading = true;
      this.$store.dispatch(this.getActionURL, this.getActionPayload)
        .then((res) => {
          this.forceClear(true, res);
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/phoneInput.scss';

  .input-label {
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    display: flex;
    align-items: center;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #888888;
  }

  .employee-field-error {
    border: 1px solid #EB5757 !important;
  }

  .employee-field {
    background: #F8F8F8;
    box-sizing: border-box;
    border-radius: 6px;
    font-style: normal;
    line-height: 15px;
    border: 1px solid #EEEEEE;
    letter-spacing: -0.005em;
    color: #333333;
  }
</style>
